/**
 * DIEUKYDIEU.TV - STORY COMPANION WIDGETS & TOC
 */
import { t } from '../i18n.js';
import { showToast } from '../ui.js';
import { renderCardHTML } from '../components/index.js';
import { getArticleHashtags, getArticleViews } from './story-content.js';

let isStoryAudioPlaying = false;

/**
 * Render 5 Most Popular Articles in companion sidebar
 */
export function renderStoryPopularArticles(art) {
    const popularContainer = document.getElementById('story-popular-list');
    if (!popularContainer) return;

    const sitemap = (typeof window !== 'undefined' && window.SITEMAP_DATA) || [];
    const allArticles = [];
    const seenIds = new Set();

    sitemap.forEach(cat => {
        if (cat.articles && Array.isArray(cat.articles)) {
            cat.articles.forEach(a => {
                if (!seenIds.has(a.id)) {
                    seenIds.add(a.id);
                    allArticles.push(a);
                }
            });
        }
    });

    const currentId = (art && art.id) ? art.id : null;
    const candidates = allArticles.filter(a => a.id !== currentId);

    // Compute synthetic sort weight based on formula in story-content.js
    function getPopularityScore(item) {
        if (typeof item.viewsNum === 'number') return item.viewsNum;
        const id = item.id || 101;
        return ((id * 149) % 2800) + 1150;
    }

    candidates.sort((a, b) => getPopularityScore(b) - getPopularityScore(a));
    const top5 = candidates.slice(0, 5);

    popularContainer.innerHTML = top5.map((item, idx) => {
        const rank = idx + 1;
        const rankStr = rank < 10 ? `0${rank}` : `${rank}`;
        const tagStr = item.tag || 'Khám phá';
        const titleSafe = (item.title || '').replace(/"/g, '&quot;');
        return `
            <div class="popular-item" data-id="${item.id}" tabindex="0" role="link" aria-label="${titleSafe}">
                <span class="popular-rank rank-${rank}">${rankStr}</span>
                <div class="popular-thumb-wrap">
                    <img class="popular-thumb" src="${item.img || ''}" alt="${titleSafe}" loading="lazy" />
                </div>
                <div class="popular-info">
                    <h5 class="popular-title">${item.title || ''}</h5>
                    <div class="popular-meta">
                        <span class="popular-tag">${tagStr}</span>
                    </div>
                </div>
            </div>
        `;
    }).join('');

    popularContainer.querySelectorAll('.popular-item').forEach((itemEl, idx) => {
        const item = top5[idx];
        if (!item) return;
        const openStory = () => {
            if (typeof window.openStoryDetail === 'function') {
                window.openStoryDetail(item);
            }
        };
        itemEl.addEventListener('click', openStory);
        itemEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openStory();
            }
        });
    });
}

/**
 * Render Sticky Companion Widgets (Master Banners & Top 5 Popular Stories)
 */
export function renderStoryCompanionWidgets(art) {
    // 1. Render Top 5 Popular Articles Widget
    renderStoryPopularArticles(art);

    // 2. Reset audio widget state if element exists
    isStoryAudioPlaying = false;
    updateStoryAudioUI();
}

/**
 * Toggle Audio Playback on Companion Card
 */
export function toggleStoryAudioPlayback() {
    isStoryAudioPlaying = !isStoryAudioPlaying;
    updateStoryAudioUI();

    if (isStoryAudioPlaying) {
        showToast('Đang phát giọng đọc thanh tịnh... 🎧');
    } else {
        showToast('Đã tạm dừng bài đọc');
    }
}

function updateStoryAudioUI() {
    const card = document.getElementById('story-audio-card');
    const btn = document.getElementById('story-audio-toggle-btn');
    const label = document.getElementById('story-audio-status-label');
    if (!card || !btn) return;

    card.classList.toggle('playing', isStoryAudioPlaying);
    const icon = btn.querySelector('.material-symbols-rounded');
    if (icon) {
        icon.innerText = isStoryAudioPlaying ? 'pause' : 'play_arrow';
    }
    if (label) {
        label.innerText = isStoryAudioPlaying ? 'Đang phát...' : 'Sẵn sàng';
    }
}

/**
 * Copy Bookmark Quote to clipboard
 */
export function copyStoryBookmarkQuote() {
    const quoteEl = document.getElementById('story-bookmark-quote-text');
    const quoteText = quoteEl ? quoteEl.innerText.trim() : 'dieukydieu.tv';
    const textToCopy = `"${quoteText.replace(/^"|"$/g, '')}" — dieukydieu.tv\n${window.location.href}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy).then(() => {
            showToast('Đã sao chép trích dẫn truyền cảm hứng! ✨');
        }).catch(() => {
            showToast('Không thể sao chép văn bản');
        });
    } else {
        showToast('Đã sao chép liên kết bài viết');
    }
}

/**
 * Render clickable Hashtags chips
 */
export function renderStoryHashtags(art, category) {
    const listEl = document.getElementById('story-hashtags-container');
    if (!listEl) return;

    const hashtags = getArticleHashtags(art, category);
    listEl.innerHTML = hashtags.map(tag => `
        <button class="hashtag-pill" data-tag="${tag}">
            <span class="hashtag-hash">#</span>
            <span class="hashtag-text">${tag.replace(/^#/, '')}</span>
        </button>
    `).join('');

    listEl.querySelectorAll('.hashtag-pill').forEach(btn => {
        btn.addEventListener('click', () => {
            const rawTag = btn.getAttribute('data-tag').replace(/^#/, '');
            showToast(`Chủ đề: #${rawTag}`);
            const searchInput = document.getElementById('desktop-search-input');
            if (searchInput && typeof window.handleDesktopSearch === 'function') {
                searchInput.value = rawTag;
                window.handleDesktopSearch(rawTag);
                const resultsEl = document.getElementById('desktop-search-results');
                if (resultsEl) resultsEl.classList.add('show');
            }
        });
    });
}

/**
 * Render Related Stories in the same category (Apple Editorial Magazine Cards)
 */
export function renderStoryRelatedArticles(art, category) {
    const gridEl = document.getElementById('story-related-grid');
    const seeMoreBtn = document.getElementById('see-more-cat-btn');
    if (!gridEl) return;

    let relatedList = [];
    if (category && category.articles) {
        relatedList = category.articles.filter(a => a.id !== art.id).slice(0, 3);
    }

    if (relatedList.length === 0) {
        const sitemap = (typeof window !== 'undefined' && window.SITEMAP_DATA) || [];
        for (const cat of sitemap) {
            if (cat.articles) {
                const candidates = cat.articles.filter(a => a.id !== art.id);
                if (candidates.length > 0) {
                    relatedList = candidates.slice(0, 3);
                    break;
                }
            }
        }
    }

    if (seeMoreBtn && category) {
        seeMoreBtn.onclick = () => {
            if (category.slug && typeof window.openCategoryBySlug === 'function') {
                window.openCategoryBySlug(category.slug);
            }
        };
    }

    gridEl.innerHTML = relatedList.map((item, idx) => renderCardHTML({
        id: item.id,
        title: item.title,
        img: item.img,
        duration: item.duration || '',
        variant: 'related',
        actionLabel: 'Đọc câu chuyện',
        legacyClass: 'related-story-card',
        staggerIndex: idx
    })).join('');

    gridEl.querySelectorAll('.related-story-card').forEach((card, idx) => {
        const item = relatedList[idx];
        if (item) {
            card.addEventListener('click', () => {
                if (typeof window.openStoryDetail === 'function') {
                    window.openStoryDetail(item);
                }
            });
        }
    });
}

/**
 * Synchronize like status across Top and Bottom Big Like Buttons
 */
